// Month calendar for the grid's date cells. Replaces the browser's date picker, which reported a change on every
// month step: the grid saved that as the date and closed (Cesar, Oct 13 and Oct 16). Here only a click on a day picks.
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "./ui";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => {
  const t = new Date();
  return iso(t.getFullYear(), t.getMonth(), t.getDate());
};

export function CalendarPopup({ value, onPick, onClear, className, style }: { value: string | null; onPick: (iso: string) => void; onClear?: () => void; className?: string; style?: React.CSSProperties }) {
  const start = value ?? todayIso();
  const [ym, setYm] = useState({ y: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) - 1 });
  const step = (k: number) => setYm(({ y, m }) => ({ y: y + Math.floor((m + k) / 12), m: (((m + k) % 12) + 12) % 12 }));
  const first = new Date(ym.y, ym.m, 1).getDay(); // 0 = Sunday
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const today = todayIso();
  const navBtn = "rounded p-1 text-slate-600 hover:bg-slate-100";
  return (
    <div
      className={cn("click-outside-ignore w-64 rounded-lg border border-slate-200 bg-surface p-2 text-[12.5px] shadow-lg", className)}
      style={style}
      // Keep focus in the cell's text box so typing still works while the calendar is open.
      onMouseDown={(e) => e.preventDefault()}
      role="dialog"
      aria-label="Pick a date"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="flex">
          <button type="button" className={navBtn} aria-label="Previous year" onClick={() => step(-12)}>
            <ChevronLeft size={14} className="-mr-2 inline" />
            <ChevronLeft size={14} className="inline" />
          </button>
          <button type="button" className={navBtn} aria-label="Previous month" onClick={() => step(-1)}>
            <ChevronLeft size={14} />
          </button>
        </span>
        <span className="font-semibold">
          {MONTHS[ym.m]} {ym.y}
        </span>
        <span className="flex">
          <button type="button" className={navBtn} aria-label="Next month" onClick={() => step(1)}>
            <ChevronRight size={14} />
          </button>
          <button type="button" className={navBtn} aria-label="Next year" onClick={() => step(12)}>
            <ChevronRight size={14} className="-mr-2 inline" />
            <ChevronRight size={14} className="inline" />
          </button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <span key={d} className="py-0.5 text-[11px] text-slate-500">
            {d}
          </span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={i} />;
          const v = iso(ym.y, ym.m, d);
          return (
            <button
              key={i}
              type="button"
              aria-label={v}
              onClick={() => onPick(v)}
              className={cn(
                "rounded py-1 hover:bg-brand-50",
                v === value && "bg-brand-500 font-semibold text-white hover:bg-brand-600",
                v !== value && v === today && "font-semibold text-brand-700 ring-1 ring-brand-500/50",
              )}
            >
              {d}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between border-t border-slate-100 pt-1">
        <button type="button" className="rounded px-2 py-0.5 text-brand-700 hover:bg-brand-50" onClick={() => onPick(today)}>
          Today
        </button>
        {onClear && (
          <button type="button" className="rounded px-2 py-0.5 text-slate-600 hover:bg-slate-100" onClick={onClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
